import React from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import { API } from '../../App';

class AdminMessages extends React.Component {
  state = {
    users: [], loading: true, page: 1, pages: 1, total: 0,
    search: '', searchInput: '', expandedUser: null, expandedQR: null,
    qrMessages: [], qrDetails: null, msgsLoading: false,
    msgPage: 1, msgPages: 1, startDate: '', endDate: ''
  };

  componentDidMount() { 
    // Check for URL parameter qr=<code>
    const params = new URLSearchParams(window.location.search);
    const qrParam = params.get('qr');
    if (qrParam) {
      // Direct QR view - load messages immediately
      this.setState({ expandedQR: qrParam, loading: false }, () => {
        this.loadMsgs();
      });
    } else {
      // Browse mode - load users list
      this.load();
    }
  }

  componentDidUpdate(_, ps) {
    if (ps.page !== this.state.page || ps.search !== this.state.search) this.load();
    if (this.state.expandedQR && (ps.expandedQR !== this.state.expandedQR || ps.msgPage !== this.state.msgPage || ps.startDate !== this.state.startDate || ps.endDate !== this.state.endDate)) this.loadMsgs();
  }

  load = async () => {
    this.setState({ loading: true });
    try {
      const t = localStorage.getItem('adminToken');
      const p = new URLSearchParams({ page: this.state.page, limit: 20 });
      if (this.state.search) p.append('search', this.state.search);
      const r = await axios.get(`${API}/admin/messages/users?${p}`, { headers: { Authorization: `Bearer ${t}` } });
      this.setState({ users: r.data.users, pages: r.data.pages, total: r.data.total, loading: false });
    } catch (e) { 
      console.error('Load users error:', e.response?.data || e.message);
      toast.error(e.response?.data?.detail || 'Failed to load users'); 
      this.setState({ loading: false }); 
    }
  };

  loadMsgs = async () => {
    this.setState({ msgsLoading: true });
    try {
      const t = localStorage.getItem('adminToken');
      const p = new URLSearchParams({ page: this.state.msgPage, limit: 50 });
      if (this.state.startDate) p.append('start_date', this.state.startDate);
      if (this.state.endDate) p.append('end_date', this.state.endDate);
      const r = await axios.get(`${API}/admin/messages/qr/${this.state.expandedQR}?${p}`, { headers: { Authorization: `Bearer ${t}` } });
      this.setState({ qrMessages: r.data.messages, qrDetails: r.data.qr_details, msgPages: r.data.pages, msgsLoading: false });
    } catch (e) { 
      console.error('Load messages error:', e.response?.data || e.message);
      toast.error(e.response?.data?.detail || 'Failed to load messages'); 
      this.setState({ msgsLoading: false }); 
    }
  };

  render() {
    const { users, loading, page, pages, total, searchInput, expandedUser, expandedQR, qrMessages, qrDetails, msgsLoading, msgPage, msgPages, startDate, endDate } = this.state;

    // Direct QR view mode (when navigating from user management)
    if (expandedQR && !expandedUser && users.length === 0) {
      return (
        <AdminLayout>
          <div className="space-y-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-white">Message Monitoring</h1>
                <p className="text-slate-400">Viewing messages for QR code</p>
              </div>
              <button onClick={() => window.location.href = '/admin/messages'} className="px-4 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-600">← Back to All Users</button>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              {this.renderMessages()}
            </div>
          </div>
        </AdminLayout>
      );
    }

    // Normal browse mode
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <div><h1 className="text-2xl font-bold text-white">Message Monitoring</h1><p className="text-slate-400">View messages by user & QR code (read-only)</p></div>
            <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">🔒 Read-only • All views logged</div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); this.setState({ search: searchInput, page: 1 }); }} className="flex gap-4">
            <input value={searchInput} onChange={(e) => this.setState({ searchInput: e.target.value })} placeholder="Search by full or partial phone..." className="flex-1 max-w-md bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white" />
            <button type="submit" className="px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-xl">Search</button>
          </form>

          <div className="text-slate-400 text-sm">{total} registered users</div>

          <div className="space-y-3">
            {loading ? <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center text-slate-400">Loading...</div> :
             users.length === 0 ? <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center text-slate-400">No users found</div> :
             users.map((u) => this.renderUser(u))}
          </div>

          {pages > 1 && (
            <div className="flex justify-between px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl">
              <button onClick={() => this.setState({ page: Math.max(1, page-1) })} disabled={page === 1} className="text-slate-400 disabled:opacity-50">← Prev</button>
              <span className="text-slate-400">{page}/{pages}</span>
              <button onClick={() => this.setState({ page: Math.min(pages, page+1) })} disabled={page === pages} className="text-slate-400 disabled:opacity-50">Next →</button>
            </div>
          )}
        </div>
      </AdminLayout>
    );
  }

  renderUser(u) {
    const { expandedUser } = this.state;
    return (
      <div key={u.id} className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <button onClick={() => this.setState({ expandedUser: expandedUser === u.id ? null : u.id, expandedQR: null })} className="w-full flex justify-between items-center p-4 hover:bg-slate-800/80">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center text-slate-400">👤</div>
            <div className="text-left">
              <p className="text-white font-mono">{u.phone}</p>
              <p className="text-slate-500 text-sm">Joined {new Date(u.created_at).toLocaleDateString('en-IN')} • {u.qr_codes.length} QR(s)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={u.is_enabled ? 'text-emerald-400 text-xs' : 'text-red-400 text-xs'}>{u.is_enabled ? 'Active' : 'Disabled'}</span>
            <span className="text-slate-400">{expandedUser === u.id ? '▼' : '▶'}</span>
          </div>
        </button>
        {expandedUser === u.id && this.renderQRList(u.qr_codes)}
      </div>
    );
  }

  renderQRList(qrCodes) {
    if (qrCodes.length === 0) return <div className="border-t border-slate-700 bg-slate-900/50 p-4 text-slate-500 text-center">No QR codes</div>;
    return (
      <div className="border-t border-slate-700 bg-slate-900/50">
        {qrCodes.map((qr) => this.renderQR(qr))}
      </div>
    );
  }

  renderQR(qr) {
    const { expandedQR } = this.state;
    const statusClass = qr.status === 'paid' ? 'bg-amber-500/20 text-amber-400' : qr.status === 'expired' || qr.status === 'disabled' ? 'bg-red-500/20 text-red-400' : 'bg-slate-500/20 text-slate-400';
    return (
      <div key={qr.id} className="border-b border-slate-700/50 last:border-0">
        <button onClick={() => this.setState({ expandedQR: expandedQR === qr.code ? null : qr.code, msgPage: 1, qrMessages: [], qrDetails: null })} className="w-full flex justify-between items-center p-4 pl-8 hover:bg-slate-800/50">
          <div className="flex items-center gap-3">
            <span className="text-slate-500">📱</span>
            <div className="text-left">
              <p className="text-white">{qr.vehicle_number} {qr.car_nickname && <span className="text-slate-500">({qr.car_nickname})</span>}</p>
              <p className="text-slate-500 text-sm font-mono">{qr.code.substring(0, 12)}...</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-2 py-1 rounded text-xs ${statusClass}`}>{qr.status}</span>
            <span className="text-slate-400 text-sm">💬 {qr.message_count}</span>
            <span className="text-slate-400">{expandedQR === qr.code ? '▼' : '▶'}</span>
          </div>
        </button>
        {expandedQR === qr.code && this.renderMessages()}
      </div>
    );
  }

  renderMessages() {
    const { qrDetails, qrMessages, msgsLoading, msgPage, msgPages, startDate, endDate } = this.state;
    return (
      <div className="bg-slate-900/80 border-t border-slate-700/50 p-4 pl-12">
        {qrDetails && (
          <div className="grid grid-cols-4 gap-4 mb-4 p-3 bg-slate-800/50 rounded-lg text-sm">
            <div><p className="text-slate-500">Owner</p><p className="text-white font-mono">{qrDetails.owner_phone}</p></div>
            <div><p className="text-slate-500">Status</p><p className="text-white">{qrDetails.status}</p></div>
            <div><p className="text-slate-500">Created</p><p className="text-white">{qrDetails.created_at ? new Date(qrDetails.created_at).toLocaleDateString('en-IN') : 'N/A'}</p></div>
            <div><p className="text-slate-500">Expires</p><p className="text-white">{qrDetails.expires_at ? new Date(qrDetails.expires_at).toLocaleDateString('en-IN') : 'Never'}</p></div>
          </div>
        )}
        <div className="flex gap-4 mb-4">
          <input type="date" value={startDate} onChange={(e) => this.setState({ startDate: e.target.value, msgPage: 1 })} className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" />
          <input type="date" value={endDate} onChange={(e) => this.setState({ endDate: e.target.value, msgPage: 1 })} className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" />
          {(startDate || endDate) && <button onClick={() => this.setState({ startDate: '', endDate: '', msgPage: 1 })} className="text-red-400 text-sm">✕ Clear</button>}
        </div>
        {msgsLoading ? <div className="text-center py-4 text-slate-400">Loading...</div> :
         qrMessages.length === 0 ? <div className="text-center py-4 text-slate-500">No messages</div> :
         <div className="space-y-2">{qrMessages.map((m, i) => this.renderMessage(m, i))}</div>}
        {msgPages > 1 && (
          <div className="flex justify-between mt-4 pt-4 border-t border-slate-700/50">
            <button onClick={() => this.setState({ msgPage: Math.max(1, msgPage-1) })} disabled={msgPage === 1} className="text-slate-400 text-sm disabled:opacity-50">← Prev</button>
            <span className="text-slate-500 text-sm">{msgPage}/{msgPages}</span>
            <button onClick={() => this.setState({ msgPage: Math.min(msgPages, msgPage+1) })} disabled={msgPage === msgPages} className="text-slate-400 text-sm disabled:opacity-50">Next →</button>
          </div>
        )}
      </div>
    );
  }

  renderMessage(m, i) {
    const isScanner = m.direction === 'scanner_to_owner' || m.direction === 'scanner_message';
    const typeClass = m.message_type === 'chat' ? 'bg-blue-500/20 text-blue-400' : m.message_type === 'emergency' ? 'bg-red-500/20 text-red-400' : 'bg-slate-500/20 text-slate-400';
    return (
      <div key={i} className="flex gap-3 p-3 bg-slate-800/50 rounded-lg">
        <span className={isScanner ? 'text-blue-400' : 'text-emerald-400'}>{isScanner ? '→' : '←'}</span>
        <div className="flex-1">
          <div className="flex gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded ${typeClass}`}>{m.message_type}</span>
            <span className="text-slate-500 text-xs">{isScanner ? 'Scanner → Owner' : 'Owner → Scanner'}</span>
          </div>
          <p className="text-white text-sm">{m.content || m.message || 'No content'}</p>
          <p className="text-slate-600 text-xs mt-1">{new Date(m.created_at).toLocaleString('en-IN')}</p>
        </div>
      </div>
    );
  }
}

export default AdminMessages;
